import PlusIcon from "@/assets/img/symbolic/create-room-plus-thin.svg?react";
import social from "@/api/social";
import ClosedHeadline from "@/assets/img/layout/profile/headline-closed.webp";
import { AnimatePresence, motion } from "framer-motion";
import IconEraser from "@/assets/img/symbolic/eraser.svg?react";
import ConfirmationPopUp from "@/components/ConfirmationPopup";
import EditorIcon from "@/assets/img/editor-symbol.svg?react";
import { useCallback, useContext, useEffect, useState } from "react";
import { PrivacyState, type DailyHeadline, type ProfileHeadline } from "@/local-shared/model";
import { renderHeadline } from "@/components/editorial/HeadlineDisplay";
import { useFullAppContext } from "@/game/AppContext";
import { socialCache } from "@/util/SocialCache";
import { AuthContext } from "@/Realize";
import HTTPStatus from "@/realize-shared/HTTPStatus";
import type { ProfileError } from "./Profile";
import UIListener from "../UIListener";

const HALF = 0.5;
const SLIDE_IN_TIMING = 0.2;

function HeadlineRenderAction({
    hasSubmitted,
    isClosed,
    index,
    onClick,
}: {
    hasSubmitted: boolean;
    isClosed?: boolean;
    index: number;
    onClick?: () => void;
})
{
    if (isClosed)
    {
        return null;
    }

    if (hasSubmitted)
    {
        return (
            <div
                className={Wind.style({
                    position: "absolute",
                    height: "h-7",
                    top: "top-0",
                    left: "left-1/2",
                    transformTranslateX: "translate-x-[-50%]",
                    transformTranslateY: "translate-y-[-50%]",

                    backgroundColor: "bg-poppy-yellow",
                    color: "text-dark-blue",
                    borderBottomRightRadius: "rounded-br-full",
                    paddingRight: "pr-5",
                    paddingLeft: "pl-4",

                    whitespace: "whitespace-nowrap",
                    textWrap: "text-nowrap",
                }).class}
            >
                You have realized this headline
            </div>
        );
    }

    return (
        <div
            className={Wind.style({
                position: "absolute",
                height: "h-7",
                top: "top-0",
                left: "left-1/2",
                transformTranslateX: "translate-x-[-50%]",
                transformTranslateY: "translate-y-[-50%]",
            }).class}
        >
            <motion.button
                className={Wind.style({
                    backgroundColor: "bg-black",
                    color: "text-white",
                    borderBottomRightRadius: "rounded-br-full",
                    paddingRight: "pr-5",
                    paddingLeft: "pl-4",
                    fontWeight: "font-semibold",

                    whitespace: "whitespace-nowrap",
                    textWrap: "text-nowrap",
                }).class}
                onClick={onClick}
                disabled={hasSubmitted}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeInOut", delay: (SLIDE_IN_TIMING * index) + HALF }}
            >
                <span
                    className={Wind.style({
                        display: "inline-block",
                    }).class + " animate-[pulse_0.5s_4_ease-in-out]"}
                >
                    Submit to this headline
                </span>
            </motion.button>
        </div>
    );
}

function HeadlineRender({
    headline,
}: {
    headline: string;
})
{
    return (
        <div
            className={Wind.style({
                fontFamily: "font-poppins",
                fontSize: "text-lg",
                fontWeight: "font-semibold",
                lineHeight: "leading-6",
                textWrap: "text-balance",
                paddingY: "py-4",
                paddingX: "px-1",

                display: "flex",
                alignItems: "items-center",
                gap: "gap-1",
                color: "text-dark-blue",
            }).class}
        >
            <div
                className={Wind.style({
                    fontFamily: "font-playfair",
                    fontSize: "text-7xl",
                    display: "flex",
                    alignItems: "items-end",
                }).class}
            >
                {headline.charAt(0)}
            </div>
            <div
                className={Wind.style({
                    textWrap: "text-balance",
                    lineHeight: "leading-[1.25rem]",
                    alignSelf: "self-stretch",
                    display: "flex",
                    alignItems: "items-center",
                }).class}
            >
                <div>
                    {renderHeadline(headline.slice(1))}
                </div>
            </div>
        </div>
    );
}

function formatHeadlineDate(date: Date): string
{
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    };

    return new Intl.DateTimeFormat("en-US", options).format(date);
}

export default function ProfileHeadlines({
    userID,
    profileID,
    profileSecurity,
    showHeadlineCreator,
    showHeadlineCamera,
    showSubmissions,
    refreshProfile,
    setContentError,
    isFollowing,
}: {
    userID: number;
    profileID: number;
    profileSecurity: PrivacyState;
    showHeadlineCreator?: () => void;
    showHeadlineCamera?: (id: number, headline: string) => void;
    showSubmissions?: (headline: DailyHeadline) => void;
    refreshProfile: () => void;
    setContentError: (error: ProfileError | null) => void;
    isFollowing: boolean;
})
{
    const userData = useContext(AuthContext);
    const { setWaiting } = useFullAppContext();
    const [headlines, setHeadlines] = useState<ProfileHeadline[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [confirmDeleteHeadline, setConfirmation] = useState<{
        headline: ProfileHeadline;
        callback: (value: boolean) => void;
    } | null>(null);

    const loadHeadlines = useCallback(() =>
    {
        Logger.log("Loading Headlines");

        const cachedHeadlines = socialCache.get<ProfileHeadline[]>(`profile_headlines_${profileID}`);

        if (cachedHeadlines)
        {
            if (headlines !== cachedHeadlines)
            {
                Logger.log("Using cached headlines for profile", profileID);
                Logger.log(cachedHeadlines);
                setHeadlines(cachedHeadlines);
            }
        }

        social.getHeadlinesForProfile(profileID).then((data) =>
        {
            if (data === null)
            {
                Logger.error("Error loading headlines", "No data returned");

                return;
            }

            if (data.status as HTTPStatus !== HTTPStatus.OK)
            {
                Logger.error("Error loading headlines", data.message);

                return;
            }

            if (data.data.length === 0)
            {
                Logger.error("Error loading headlines", "No headlines found for this profile");

                return;
            }

            // Check if new headlines is different than cached headlines

            if (cachedHeadlines)
            {
                Logger.log(cachedHeadlines);

                const isEqual = JSON.stringify(cachedHeadlines) === JSON.stringify(data.data);

                if (isEqual)
                {
                    Logger.log("Cached headlines are up to date, not updating state");

                    return;
                }
            }

            socialCache.set(`profile_headlines_${profileID}`, data.data, 1000 * 60 * 60); // Cache for 1 hour

            setHeadlines(data.data);
        })
            .catch((error) =>
            {
                Logger.error("Error loading headlines", error);
            });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileID]);

    // Load headlines when component mounts
    useEffect(() =>
    {
        setIsLoading(true);
        setContentError(null);

        if (!profileID || profileID === 0)
        {
            return;
        }

        if (!userData)
        {
            return;
        }

        if ((profileSecurity === PrivacyState.FOLLOWERS || profileSecurity === PrivacyState.BOTH) && !isFollowing)
        {
            Logger.log("Profile is set to followers only, but user is not following the profile");
            setContentError("NotAFollower");

            return;
        }


        (async () =>
        {
            if (profileSecurity === PrivacyState.FOLLOWING || profileSecurity === PrivacyState.BOTH)
            {
                if (!await social.profileFollowingMe(profileID))
                {
                    Logger.log("Profile is set to following only, but the profile doesn't follow you.");
                    setContentError("NotFollowing");

                    return;
                }
            }


            loadHeadlines();
        })().catch((error) =>
        {
            Logger.error("Error loading profile headlines:", error);
            setHeadlines([]);
        })
            .finally(() =>
            {
                setIsLoading(false);
            });
    }, [isFollowing, loadHeadlines, profileID, profileSecurity, setContentError, userData]);

    useEffect(() =>
    {
        const loadAgain = () =>
        {
            Logger.log("Refreshing Profile Headlines");

            setIsLoading(true);
            setContentError(null);

            if (!profileID || profileID === 0)
            {
                return;
            }

            if (!userData)
            {
                return;
            }

            if ((profileSecurity === PrivacyState.FOLLOWERS || profileSecurity === PrivacyState.BOTH) && !isFollowing)
            {
                Logger.log("Profile is set to followers only, but user is not following the profile");
                setContentError("NotAFollower");

                return;
            }


            (async () =>
            {
                if (profileSecurity === PrivacyState.FOLLOWING || profileSecurity === PrivacyState.BOTH)
                {
                    if (!await social.profileFollowingMe(profileID))
                    {
                        Logger.log("Profile is set to following only, but the profile doesn't follow you.");
                        setContentError("NotFollowing");

                        return;
                    }
                }


                loadHeadlines();
            })().catch((error) =>
            {
                Logger.error("Error loading profile headlines:", error);
                setHeadlines([]);
            })
                .finally(() =>
                {
                    setIsLoading(false);
                });
        };

        UIListener.listen("RefreshProfileTab", loadAgain);

        return () =>
        {
            UIListener.unlisten("RefreshProfileTab", loadAgain);
        };
    }, [isFollowing, loadHeadlines, profileID, profileSecurity, setContentError, userData]);

    if (headlines.length === 0 && !isLoading)
    {
        return (
            <motion.div
                className={Wind.style({
                    width: "w-full",
                    height: "h-full",
                    display: "flex",
                    flexDirection: "flex-col",
                    gap: "gap-3",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    color: "text-[#695946]",
                }).class}

                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
            >
                <EditorIcon
                    className={Wind.style({
                        width: "w-32",
                        aspectRatio: "aspect-[240/203]",
                        height: "h-auto",
                        marginX: "mx-auto",
                        objectFit: "object-contain",
                    }).class}
                />
                <h1
                    className={Wind.style({
                        fontFamily: "font-poppins",
                        fontSize: "text-xl",
                        fontWeight: "font-bold",
                        lineHeight: "leading-6",
                        letterSpacing: "tracking-[0%]",
                        textAlign: "text-center",
                    }).class}
                >
                    Nothing yet!
                </h1>
                <p
                    className={Wind.style({
                        fontFamily: "font-poppins",
                        fontSize: "text-base",
                        fontWeight: "font-normal",
                        lineHeight: "leading-6",
                        textWrap: "text-balance",
                        textAlign: "text-center",
                        maxWidth: "max-w-64"
                    }).class}
                >
                    {userID === profileID
                        ? (
                            <div>
                                You haven&apos;t created any headlines yet!
                                <button
                                    className={Wind.style({
                                        marginTop: "mt-2",
                                        display: "flex",
                                        fontWeight: "font-bold",
                                        flexDirection: "flex-row",
                                        color: "text-[#695946]",
                                        borderColor: "border-[#695946]",
                                        borderWidth: "border",
                                        marginX: "mx-auto",
                                        paddingY: "py-2",
                                        paddingX: "px-4",
                                        borderRadius: "rounded-lg",
                                        alignItems: "items-center",
                                        justifyContent: "justify-center",
                                        gap: "gap-2",
                                    }).class}
                                    onClick={() =>
                                    {
                                        showHeadlineCreator?.();
                                    }}
                                >
                                    <PlusIcon className="h-6 w-6" />

                                </button>
                            </div>
                        ) : "Once they create a headline for you to submit to they'll appear here."
                    }

                </p>
            </motion.div>
        );
    }

    return (
        <>
            <div
                className={Wind.style({
                    paddingY: "py-2",
                    paddingX: "px-4",
                    display: "flex",
                    flexDirection: "flex-col",
                    gap: "gap-4",
                }).class}
            >
                <AnimatePresence>
                    {headlines.map((item, index) => (
                        <motion.div
                            key={item.id}
                            className={Wind.style({
                                display:"flex",
                                flexDirection: "flex-col",
                                backgroundColor: "bg-white",
                                paddingX: "px-4",
                                paddingBottom: "pb-4",
                                position: "relative",
                                marginTop: "mt-4",
                            }).class}
                            onClick={() =>
                            {
                                if (item.submissions > 0)
                                {
                                    // Get Dailyheadline type
                                    social.getDailyHeadlineByID(item.id).then((dailyHeadline) =>
                                    {
                                        if (dailyHeadline)
                                        {
                                            if (dailyHeadline.data)
                                            {
                                                Logger.log(
                                                    "Loading submissions for headline ID:",
                                                    dailyHeadline.data.id
                                                );

                                                showSubmissions?.(dailyHeadline.data);
                                            }
                                        }
                                    })
                                        .catch((error) =>
                                        {
                                            Logger.error("Error loading headline submissions:", error);
                                        });
                                }
                            }}

                            initial={{ opacity: 0, scale: 0.95, x: "100%" }}
                            animate={{ opacity: 1, scale: 1, x: "0%" }}
                            exit={{ opacity: 0, scale: 0.95, x: "-100%" }}
                            transition={{ duration: SLIDE_IN_TIMING, delay: (SLIDE_IN_TIMING / 2) * index }}
                        >
                            {userID === profileID && !item.closed && (
                                <button
                                    className={Wind.style({
                                        backgroundColor: "bg-sharp-red",
                                        color: "text-white",
                                        position: "absolute",
                                        top: "top-0",
                                        transformTranslateY: "translate-y-[-25%]",
                                        right: "right-2",
                                        paddingX: "px-2",
                                        paddingY: "py-1",
                                        borderRadius: "rounded-md",
                                    }).class}
                                    onClick={(e) =>
                                    {
                                        e.stopPropagation();

                                        // Handle delete action
                                        setConfirmation({
                                            headline: item,
                                            callback: (value: boolean) =>
                                            {
                                                if (value)
                                                {
                                                    setWaiting(true);

                                                    social.profile.deleteHeadline(item.id).then((success) =>
                                                    {
                                                        if (success)
                                                        {
                                                            Logger.log("Deleted headline:", item.id);
                                                            setConfirmation(null);
                                                            refreshProfile();

                                                            UIListener.trigger("RefreshProfileTab", {});
                                                        }
                                                        else
                                                        {
                                                            Logger.error("Failed to delete headline:", item.id);
                                                        }
                                                    })
                                                        .catch((error) =>
                                                        {
                                                            Logger.error("Error deleting headline:", error);
                                                        })
                                                        .finally(() =>
                                                        {
                                                            setWaiting(false);
                                                        });
                                                }
                                            },
                                        });
                                    }}
                                >
                                    <IconEraser
                                        className={Wind.style({
                                            width: "w-6",
                                            height: "h-6",
                                        }).class}
                                    />
                                </button>
                            )}
                            {userID !== profileID && (
                                <HeadlineRenderAction
                                    onClick={() =>
                                    {
                                        showHeadlineCamera?.(item.id, item.headline);
                                    }}
                                    hasSubmitted={item.hasSubmitted}
                                    isClosed={item.closed}
                                    index={index}
                                />
                            )}
                            <HeadlineRender
                                headline={item.headline}

                            />
                            {item.closed && (
                                <img
                                    src={ClosedHeadline}
                                    className={Wind.style({
                                        position: "absolute",
                                        top: "top-0",
                                        right: "right-0",
                                        transformRotate: "rotate-[30deg]",
                                        width: "w-24",
                                        aspectRatio: "aspect-[468/181]",
                                    }).class}
                                />
                            )}
                            <div
                                className={Wind.style({
                                    width: "w-full",
                                    borderTopWidth: "border-t",
                                    borderTopColor: "border-t-[#C9C9C9]",
                                    paddingTop: "pt-3",
                                    display: "flex",
                                    alignItems: "items-center",
                                    justifyContent: "justify-between",

                                    fontFamily: "font-inter",
                                    fontSize: "text-xs",
                                    fontWeight: "font-normal",
                                    lineHeight: "leading-3",

                                    color: "text-dark-blue/80"
                                }).class}
                            >
                                <div>
                                    {`${item.submissions} Realization${item.submissions !== 1 ? "s" : ""}`}
                                </div>
                                <div>
                                    {formatHeadlineDate(new Date(item.created_at))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <AnimatePresence>
                {confirmDeleteHeadline && (
                    <ConfirmationPopUp
                        onClose={() =>
                        {
                            setConfirmation(null);
                        }}

                        onConfirm={() =>
                        {
                            confirmDeleteHeadline.callback(true);
                        }}

                        title="Delete Headline?"
                        confirmText="Yes"
                        showCancelButton
                    >
                        <p>Are you sure you want to delete this headline?</p>
                        <p className="italic font-bold mb-2">
                            {confirmDeleteHeadline.headline.headline}
                        </p>
                        <p className="text-red-500 italic text-sm">
                            This action cannot be undone and any current realizations will also be deleted.
                        </p>
                    </ConfirmationPopUp>
                )}
            </AnimatePresence>
        </>
    );
}
